
import React, { useState, useContext, useRef, useEffect, useMemo, memo } from 'react';
import { Puzzle, Page, PageContextType, defaultImageEdits, getSafeImageEdits, Timestamp } from '../types';
import { useAuth, playSound } from '../AuthContext';
import { isUserOnline } from '../utils';
import { useAdmin } from '../AdminContext';
import { usePuzzles } from '../PuzzleContext';
import { Timestamp as TimestampValue } from '../firebase';
import CommentSection from './CommentSection';
import ReportPostModal from './ReportPostModal';
import UseClueModal from './UseClueModal';
import WrongGuessModal from './WrongGuessModal';
import { PageContext } from '../PageContext';
import ShareModal from './ShareModal';
import ConfirmationModal from './ConfirmationModal';
import AIConsentModal from './AIConsentModal';
import { getAIGeneratedHint } from '../gemini';
import { AdService } from '../AdService';
import { sendDirectNotification } from '../notificationUtils';
import { useAppContext } from '../AppContext';


interface PostCardProps {
    puzzle: Puzzle;
    isClickable?: boolean;
    showCommentsInitially?: boolean;
    suggestions?: Puzzle[];
    onEdit?: (puzzle: Puzzle) => void;
    onReport?: (puzzleId: string, reason: string, details: string) => void;
    isPreview?: boolean;
    hideComments?: boolean;
    hideActions?: boolean;
    hideHeader?: boolean;
}

const TaggedContent: React.FC<{ text: string; userId: string; baseClassName?: string; }> = ({ text, userId, baseClassName }) => {
    const { allUsers } = useAuth();
    const pageContext = useContext(PageContext);
    const creator = allUsers.find(u => u.uid === userId);
    const isBusiness = creator?.isBusiness || false;

    const handleTagClick = (e: React.MouseEvent, type: 'mention' | 'hashtag', value: string) => {
        e.stopPropagation();
        if (type === 'hashtag') {
            pageContext?.switchPage(Page.FilteredFeed, value, { hashtag: value });
        } else if (type === 'mention') {
            const mentionedUser = allUsers.find(u => u.displayName.toLowerCase() === value.toLowerCase());
            if (mentionedUser) {
                pageContext?.switchPage(Page.Profile, mentionedUser.displayName, { userId: mentionedUser.uid });
            }
        }
    };

    const parts = text.split(/(https?:\/\/[^\s]+|#[a-zA-Z0-9_]+|@[a-zA-Z0-9_]+)/g);

    return (
        <p className={baseClassName}>
            {parts.map((part, i) => {
                if (part.startsWith('http')) {
                    if (isBusiness) {
                        return (
                            <a 
                                key={i} 
                                href={part} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-400 hover:underline font-semibold"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {part}
                            </a>
                        );
                    }
                    return <span key={i} className="opacity-70 italic">{part}</span>;
                }
                if (part.startsWith('#')) {
                    return (
                        <button key={i} onClick={(e) => handleTagClick(e, 'hashtag', part)} className="font-semibold text-blue-400 hover:underline focus:outline-none">
                            {part}
                        </button>
                    );
                }
                if (part.startsWith('@')) {
                    const username = part.substring(1);
                    return (
                        <button key={i} onClick={(e) => handleTagClick(e, 'mention', username)} className="font-semibold text-pink-400 hover:underline focus:outline-none">
                            {part}
                        </button>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </p>
    );
};

const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const hStr = h > 0 ? String(h).padStart(2, '0') + ':' : '';
    const mStr = String(m).padStart(2, '0');
    const sStr = String(s).padStart(2, '0');
    
    return `${hStr}${mStr}:${sStr}`;
};

const PostCard: React.FC<PostCardProps> = ({ puzzle, isClickable = true, showCommentsInitially = false, suggestions, onEdit, onReport, isPreview = false, hideComments = false, hideActions = false, hideHeader = false }) => {
    const pageContext = useContext(PageContext);
    const { userProfile, updateScore, useClue, deductPoints, toggleFollow, groups, updateGroupScore, deductGroupPoints, allUsers, addFreeClue, toggleBlockUser, useAICredit, addPointsToUser } = useAuth();
    const { addNotification, sendFriendRequest } = useAdmin();
    const { deletePuzzle, recordPlayerScore, updatePuzzleReactions, recordGuessAttempt } = usePuzzles();
    const { appConfig } = useAppContext();
    const [now, setNow] = useState(() => Date.now());

    const [guess, setGuess] = useState('');
    const [showComments, setShowComments] = useState(showCommentsInitially);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [reportModalTriggerRect, setReportModalTriggerRect] = useState<DOMRect | null>(null);
    const [isClueModalOpen, setClueModalOpen] = useState(false);
    const [clueModalTriggerRect, setClueModalTriggerRect] = useState<DOMRect | null>(null);
    const [isShareModalOpen, setShareModalOpen] = useState(false);
    const [shareModalTriggerRect, setShareModalTriggerRect] = useState<DOMRect | null>(null);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmModalProps, setConfirmModalProps] = useState({ title: '', message: '', onConfirm: () => {}, triggerRect: null as DOMRect | null, confirmText: 'Confirm' });
    const [isGeneratingHint, setIsGeneratingHint] = useState(false);
    const [showAIConsentModal, setShowAIConsentModal] = useState(false);


    // Modal states
    const [showWrongGuess, setShowWrongGuess] = useState(false);
    const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);

    // Audio states
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [areControlsVisible, setAreControlsVisible] = useState(true);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    const resetControlsTimeout = () => {
        setAreControlsVisible(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
            if (isAudioPlaying) setAreControlsVisible(false);
        }, 2500);
    };

    useEffect(() => {
        if (!isAudioPlaying) {
            setAreControlsVisible(true);
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        } else {
            resetControlsTimeout();
        }
    }, [isAudioPlaying]);
    
    // Zoom State (Puppeteer Strategy)
    const [zoomState, setZoomState] = useState({ scale: 1, x: 0, y: 0 });
    const interactionRef = useRef({
        startDist: 0,
        startScale: 1,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
        isPinching: false,
        isPanning: false
    });
    
    // Refs
    const optionsMenuRef = useRef<HTMLDivElement>(null);
    const reportButtonRef = useRef<HTMLButtonElement>(null);
    const clueButtonRef = useRef<HTMLButtonElement>(null);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const postCardRef = useRef<HTMLDivElement>(null);
    const blockButtonRef = useRef<HTMLButtonElement>(null);
    const deleteButtonRef = useRef<HTMLButtonElement>(null);

    // Get live profile from allUsers if available, but fall back to embedded data
    const creator = useMemo(() => allUsers.find(u => u.uid === puzzle.creatorId), [puzzle.creatorId, allUsers]);

    // Use live data if available, otherwise use the data embedded in the puzzle.
    // This makes the card render immediately and update if the live profile changes.
    const creatorDisplayName = creator?.displayName || puzzle.creatorProfile.displayName;
    const creatorPhotoURL = creator?.photoURL || puzzle.creatorProfile.photoURL;
    const isCreatorOnline = isUserOnline(creator);


    const isSolved = useMemo(() => {
        if (!userProfile) return false;
        if (puzzle.type === 'timed') {
            return !!(puzzle.playerScores && puzzle.playerScores[userProfile.uid]);
        }
        if (puzzle.groupId) {
            const group = groups.find(g => g.id === puzzle.groupId);
            if (!group) return false;
            const userSolvedInGroup = group.solvedPuzzles[userProfile.uid] || [];
            return userSolvedInGroup.includes(puzzle.id);
        } else {
            return userProfile.solvedPuzzles.includes(puzzle.id);
        }
    }, [userProfile, puzzle, groups]);
    
    const currentAudioStartTime = useMemo(() => isSolved ? (puzzle.answerAudioStartTime ?? 0) : (puzzle.audioStartTime ?? 0), [isSolved, puzzle.answerAudioStartTime, puzzle.audioStartTime]);
    const currentAudioEndTime = useMemo(() => isSolved ? (puzzle.answerAudioEndTime ?? Number.MAX_SAFE_INTEGER) : (puzzle.audioEndTime ?? Number.MAX_SAFE_INTEGER), [isSolved, puzzle.answerAudioEndTime, puzzle.audioEndTime]);

    const revealedIndices = useMemo(() => {
        const revealedForThisPuzzle = userProfile?.revealedClues?.[puzzle.id] || [];
        return new Set(revealedForThisPuzzle);
    }, [userProfile?.revealedClues, puzzle.id]);

    const initialRevealedString = useMemo(() => {
        return puzzle.answer.split('').map((char, index) => {
            if (char === ' ') return ' ';
            if (revealedIndices.has(index)) return char.toUpperCase();
            return ' '; // Placeholder for non-revealed letters
        }).join('');
    }, [puzzle.answer, revealedIndices]);

    const isOwnPost = userProfile?.uid === puzzle.creatorId;
    const canPlayAIChallenge = userProfile?.uid === puzzle.creatorId && puzzle.type === 'ai';
    
    const isFollowingCreator = useMemo(() => userProfile?.following.includes(puzzle.creatorId) ?? false, [userProfile?.following, puzzle.creatorId]);

    const imageEdits = useMemo(() => getSafeImageEdits(puzzle.imageEdits), [puzzle.imageEdits]);
    const imageHeightClass = 'h-64';
    
    useEffect(() => {
        setGuess(initialRevealedString);
        inputsRef.current = new Array(puzzle.answer.length);
    }, [puzzle.id, initialRevealedString]);

    useEffect(() => {
        if (puzzle.type === 'timed' && puzzle.gameStartTime) {
            const timer = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(timer);
        }
    }, [puzzle.type, puzzle.gameStartTime]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
                setShowOptionsMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-play audio when scrolling into view
    useEffect(() => {
        if (!puzzle.audioUrl || !postCardRef.current || isPreview) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (audioRef.current && audioRef.current.paused) {
                        try {
                            audioRef.current.currentTime = currentAudioStartTime;
                        } catch(e) {}
                        const playPromise = audioRef.current.play();
                        if (playPromise !== undefined) {
                            playPromise.then(() => setIsAudioPlaying(true)).catch(() => {
                                setIsAudioPlaying(false);
                            });
                        }
                    }
                } else {
                    if (audioRef.current && !audioRef.current.paused) {
                        audioRef.current.pause();
                        setIsAudioPlaying(false);
                    }
                }
            });
        }, {
            threshold: 0.6 // Try to play when 60% of the post is visible
        });

        const currentRef = postCardRef.current;
        observer.observe(currentRef);
        return () => observer.unobserve(currentRef);
    }, [puzzle.audioUrl, currentAudioStartTime, isPreview]);
    
    const handleCloseFeedbackModals = () => {
        setShowWrongGuess(false);
    };

    const handleCardClick = () => {
        if (isClickable && !isPreview) {
            AdService.registerClick();
            pageContext?.switchPage(Page.PostDetail, 'Post', { puzzle });
        }
    };

    const handleProfileClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPreview) return;
        pageContext?.switchPage(Page.Profile, creatorDisplayName, { userId: puzzle.creatorId });
    };

    // --- ZOOM EVENT HANDLERS ---
    const handleTouchStart = (e: React.TouchEvent) => {
        if (isClickable) return; // Only enable on single post view

        if (e.touches.length === 2) {
            interactionRef.current.isPinching = true;
            interactionRef.current.isPanning = false; 
            interactionRef.current.startDist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            interactionRef.current.startScale = zoomState.scale;
        } else if (e.touches.length === 1 && zoomState.scale > 1) {
            interactionRef.current.isPanning = true;
            interactionRef.current.startX = e.touches[0].clientX;
            interactionRef.current.startY = e.touches[0].clientY;
            interactionRef.current.startPanX = zoomState.x;
            interactionRef.current.startPanY = zoomState.y;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (isClickable) return;

        if (interactionRef.current.isPinching && e.touches.length === 2) {
            e.preventDefault(); 
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            // Limit zoom to 4x, min 1x
            const newScale = Math.min(Math.max(1, interactionRef.current.startScale * (dist / interactionRef.current.startDist)), 4);
            setZoomState(prev => ({ ...prev, scale: newScale }));
        } else if (interactionRef.current.isPanning && e.touches.length === 1) {
            e.preventDefault();
            const dx = e.touches[0].clientX - interactionRef.current.startX;
            const dy = e.touches[0].clientY - interactionRef.current.startY;
            // 1:1 Panning response
            setZoomState(prev => ({ 
                ...prev, 
                x: interactionRef.current.startPanX + dx, 
                y: interactionRef.current.startPanY + dy 
            }));
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (isClickable) return;
        
        if (e.touches.length === 0) {
            interactionRef.current.isPinching = false;
            interactionRef.current.isPanning = false;

            // Snap back if scale is near 1
            if (zoomState.scale < 1.1) {
                setZoomState({ scale: 1, x: 0, y: 0 });
            }
        } else if (e.touches.length === 1 && interactionRef.current.isPinching) {
            interactionRef.current.isPinching = false;
        }
    };

    const zoomStyle: React.CSSProperties = !isClickable ? {
        transform: `translate(${zoomState.x}px, ${zoomState.y}px) scale(${zoomState.scale})`,
        transition: interactionRef.current.isPinching || interactionRef.current.isPanning ? 'none' : 'transform 0.3s ease-out',
        zIndex: zoomState.scale > 1 ? 50 : 'auto', 
        cursor: zoomState.scale > 1 ? 'grab' : 'default',
        touchAction: zoomState.scale > 1 ? 'none' : 'pan-y'
    } : {};
    
    // --- END ZOOM HANDLERS ---

    const handleGuessSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Guest Logic
        if (!userProfile && !isPreview) {
            pageContext?.switchPage(Page.Login, 'Sign In');
            return;
        }

        if (isSolved || (isOwnPost && !canPlayAIChallenge && !isPreview) || guess === initialRevealedString) return;
        
        const sanitizedGuess = guess.toLowerCase();
        const sanitizedAnswer = puzzle.answer.toLowerCase();
        const isCorrect = sanitizedGuess === sanitizedAnswer;

        if (!isPreview && userProfile) {
            recordGuessAttempt(puzzle.id, userProfile.uid, isCorrect, guess);
        }

        if (isCorrect) {
            if (puzzle.audioUrl && audioRef.current) {
                // If it's an audio puzzle, play it when correct guess happens
                audioRef.current.currentTime = puzzle.answerAudioStartTime ?? (puzzle.audioStartTime || 0);
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => setIsAudioPlaying(true)).catch(() => setIsAudioPlaying(false));
                } else {
                    setIsAudioPlaying(true);
                }
            }

            const pointsAwarded = 5;
            if (!isPreview && userProfile) {
                if (puzzle.groupId) {
                    updateGroupScore(puzzle.groupId, pointsAwarded, puzzle.id);
                } else {
                    updateScore(pointsAwarded, puzzle);
                }
                
                try {
                    if(addNotification) {
                        addNotification(userProfile.uid, {
                            type: 'guess_correct',
                            text: `You solved "${puzzle.clue}" and earned ${pointsAwarded} points!`,
                            puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                        }).catch(err => console.error("Notification error:", err));
                    }
                    
                    if (!isOwnPost) {
                        sendDirectNotification(puzzle.creatorId, {
                            type: 'guess_received',
                            text: `solved your puzzle "${puzzle.clue.substring(0, 30)}..."!`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: {
                                id: puzzle.id,
                                clue: puzzle.clue,
                                imageUrl: puzzle.imageUrl
                            }
                        });
                    }

                } catch(e) { console.error("Notification failed", e); }
            }
             const soundToPlay = userProfile?.correctGuessSound || 'Triumph';
            if (soundToPlay !== 'None') playSound(soundToPlay);
            if (!isPreview) pageContext?.launchCelebrationModal(puzzle, suggestions);
            else alert("Correct! (This is just a preview)");
        } else {
             const pointsDeducted = 1;
             if (!isPreview && userProfile) {
                 if (puzzle.groupId) {
                    deductGroupPoints(puzzle.groupId, pointsDeducted);
                 } else {
                    deductPoints(pointsDeducted);
                 }

                 if (!isOwnPost) {
                    addPointsToUser(puzzle.creatorId, 1);
                 }

                 try {
                     if (addNotification) {
                         addNotification(userProfile.uid, {
                             type: 'guess_incorrect',
                             text: `Your guess for "${puzzle.clue}" was incorrect. You lost ${pointsDeducted} point.`,
                             puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                         }).catch(err => console.error("Notification error:", err));
                     }

                     if (!isOwnPost) {
                        sendDirectNotification(puzzle.creatorId, {
                            type: 'guess_received',
                            text: `guessed incorrectly on your puzzle. You score 1 point!`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: {
                                id: puzzle.id,
                                clue: puzzle.clue,
                                imageUrl: puzzle.imageUrl
                            }
                        });
                    }

                 } catch (e) {
                     console.error("Failed to add incorrect guess notification", e);
                 }
             }
             const soundToPlay = userProfile?.wrongGuessSound || 'Buzz';
             if(soundToPlay !== 'None') playSound(soundToPlay);
             
             setShowWrongGuess(true);
             setGuess(initialRevealedString);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value.toUpperCase();
        const newGuess = guess.split('');
        newGuess[index] = value || ' ';
        setGuess(newGuess.join(''));

        if (value && index < puzzle.answer.length - 1) {
            let nextIndex = index + 1;
            while (nextIndex < puzzle.answer.length && (puzzle.answer[nextIndex] === ' ' || revealedIndices.has(nextIndex))) {
                nextIndex++;
            }
            if (inputsRef.current[nextIndex]) {
                inputsRef.current[nextIndex]?.focus();
            } else {
                inputsRef.current[index]?.blur();
            }
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !e.currentTarget.value) {
            e.preventDefault(); 
            let prevIndex = index - 1;
            while (prevIndex >= 0 && (puzzle.answer[prevIndex] === ' ' || revealedIndices.has(prevIndex))) {
                prevIndex--;
            }
            if (inputsRef.current[prevIndex]) {
                inputsRef.current[prevIndex]?.focus();
            }
        }
    };

    const handleAnswerCardClick = () => {
        // Guest Logic
        if (!userProfile && !isPreview) {
            pageContext?.switchPage(Page.Login, 'Sign In');
            return;
        }

        if (isClickable && !isPreview) {
            AdService.registerClick();
            pageContext?.switchPage(Page.PostDetail, 'Post', { puzzle });
            return;
        }

        if (!isPreview && isOwnPost && !canPlayAIChallenge) {
            alert("You created this puzzle! You cannot guess on your own posts.");
            return;
        }
        
        if (isSolved) return;

        const firstEmptyInputIndex = inputsRef.current.findIndex((input, index) => {
            return input && !input.disabled && guess[index] === ' ';
        });
        
        if (firstEmptyInputIndex !== -1) {
            inputsRef.current[firstEmptyInputIndex]?.focus();
        } else {
            const firstEnabledInput = inputsRef.current.find(input => input && !input.disabled);
            if (firstEnabledInput) {
                firstEnabledInput.focus();
            }
        }
    };
    
    const handleFollowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!userProfile) {
            pageContext?.switchPage(Page.Login, 'Sign In');
            return;
        }

        if (isFollowingCreator) {
            // Unfriend logic (Stay with raw toggleFollow or add confirmation)
            if (window.confirm(`Unfriend ${creatorDisplayName}?`)) {
                toggleFollow(puzzle.creatorId);
                const soundToPlay = userProfile?.uiClickSound || 'Pop';
                if (soundToPlay !== 'None') playSound(soundToPlay);
            }
        } else {
            // Using sendFriendRequest from AdminContext correctly sends notification
            sendFriendRequest(puzzle.creatorId);
        }
    };

    const handleReaction = (type: 'likes' | 'dislikes' | 'hearts') => {
        if (!userProfile) {
            if (!isPreview) pageContext?.switchPage(Page.Login, 'Sign In');
            return;
        }
        if (isPreview) return;

        const soundToPlay = userProfile?.uiClickSound || 'Pop';
        if (soundToPlay !== 'None') playSound(soundToPlay);

        const currentIsLiked = puzzle.likes.includes(userProfile.uid);
        const currentIsDisliked = puzzle.dislikes.includes(userProfile.uid);
        const currentIsHearted = puzzle.hearts.includes(userProfile.uid);

        if (type === 'likes') {
            updatePuzzleReactions(puzzle.id, 'likes', userProfile.uid, !currentIsLiked);
            if (currentIsDisliked) updatePuzzleReactions(puzzle.id, 'dislikes', userProfile.uid, false);
        } else if (type === 'dislikes') {
            updatePuzzleReactions(puzzle.id, 'dislikes', userProfile.uid, !currentIsDisliked);
            if (currentIsLiked) updatePuzzleReactions(puzzle.id, 'likes', userProfile.uid, false);
            if (currentIsHearted) updatePuzzleReactions(puzzle.id, 'hearts', userProfile.uid, false);
        } else if (type === 'hearts') {
            updatePuzzleReactions(puzzle.id, 'hearts', userProfile.uid, !currentIsHearted);
            if (currentIsDisliked) updatePuzzleReactions(puzzle.id, 'dislikes', userProfile.uid, false);
        }
    };

    const handleCommentToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Allow viewing comments, but posting is handled inside CommentSection
        const soundToPlay = userProfile?.uiClickSound || 'Pop';
        if (soundToPlay !== 'None') playSound(soundToPlay);
        setShowComments(!showComments);
    };

    const handleReportSubmit = (reason: string, details: string) => {
        setReportModalOpen(false);
        setShowOptionsMenu(false);
        if (onReport) {
            onReport(puzzle.id, reason, details);
            alert('Thank you for your report. Our moderation team will review it shortly.');
        } else {
            console.warn("onReport handler not provided to PostCard");
        }
    };

    const handleDeleteClick = () => {
        setShowOptionsMenu(false);
        setConfirmModalProps({
            title: 'Delete Post?',
            message: 'This will permanently delete this post from the app. This action cannot be undone.',
            onConfirm: () => {
                deletePuzzle(puzzle.id);
                setConfirmModalOpen(false);
            },
            triggerRect: deleteButtonRef.current?.getBoundingClientRect() || null,
            confirmText: 'Delete',
        });
        setConfirmModalOpen(true);
    };

    const handleBlockUserClick = () => {
        setShowOptionsMenu(false);
        if (!creator) return;
        setConfirmModalProps({
            title: `Block ${creator.displayName}?`,
            message: 'They will not be able to find your profile, posts, or message you. They will not be notified that you have blocked them.',
            onConfirm: () => {
                toggleBlockUser(creator.uid);
                setConfirmModalOpen(false);
            },
            triggerRect: blockButtonRef.current?.getBoundingClientRect() || null,
            confirmText: 'Block',
        });
        setConfirmModalOpen(true);
    };

    const handleUseClue = () => {
        if (!userProfile || userProfile.clues <= 0) {
            setClueModalOpen(false);
            return;
        }

        if (!isPreview && isOwnPost && !canPlayAIChallenge) {
            setClueModalOpen(false);
            return;
        }

        const answerChars = puzzle.answer.split('');
        const unrevealedLetterIndices = answerChars
            .map((char, index) => ({ char, index }))
            .filter(item => {
                const isCorrectlyGuessed = guess[item.index]?.toLowerCase() === item.char.toLowerCase();
                return item.char !== ' ' && !revealedIndices.has(item.index) && !isCorrectlyGuessed;
            })
            .map(item => item.index);

        if (unrevealedLetterIndices.length === 0) {
            setClueModalOpen(false);
            return;
        }

        const randomIndex = unrevealedLetterIndices[Math.floor(Math.random() * unrevealedLetterIndices.length)];
        
        useClue(puzzle.id, randomIndex);

        const soundToPlay = userProfile?.uiClueRevealSound || 'Magic';
        if (soundToPlay !== 'None') playSound(soundToPlay);
        
        try {
            if(addNotification && userProfile) {
                addNotification(userProfile.uid, {
                    type: 'guess_correct', 
                    text: `You used a clue on "${puzzle.clue}". A letter was revealed!`,
                    puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                }).catch(err => console.error(err));
            }
        } catch(e) { console.error(e); }

        setClueModalOpen(false);
    };
    
    const handlePurchaseClues = () => {
        setClueModalOpen(false);
        pageContext?.switchPage(Page.ClueStore, 'Clue Store');
    };
    
    const handleWatchAd = (onComplete: () => void) => {
        setClueModalOpen(false);
        AdService.showRewardedAd(() => {
            addFreeClue();
            onComplete();
        });
    };
    
    const handleOpenClueModalFromWrongGuess = () => {
        setShowWrongGuess(false);
        if (clueButtonRef.current) {
            setClueModalTriggerRect(clueButtonRef.current.getBoundingClientRect());
        }
        setClueModalOpen(true);
    };
    
    const handleGetAIHint = async (e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!userProfile) return;

        if (!userProfile.aiConsentEnabled) {
            setShowAIConsentModal(true);
            return;
        }
    
        if (userProfile.aiCredits <= 0) {
            pageContext?.switchPage(Page.AIHome, "AI Creator Studio");
            return;
        }
    
        setIsGeneratingHint(true);
        const creditUsed = await useAICredit();
    
        if (creditUsed) {
            const hint = await getAIGeneratedHint(puzzle.id, puzzle.clue, puzzle.answer);
            if (hint) {
                alert(`💡 AI Hint: ${hint}`);
            } else {
                alert("The AI couldn't generate a hint this time.");
            }
        } else {
            console.error("Could not use an AI credit. Please try again.");
        }
        setIsGeneratingHint(false);
    };

    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    const renderPuzzleContent = () => {
        const { adjustments } = imageEdits;
        const imageStyle: React.CSSProperties = {
            filter: `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturate}%) blur(${adjustments.blur}px)`,
            transform: `scale(${adjustments.zoom}) translate(${adjustments.offsetX}px, ${adjustments.offsetY}px)`,
            transition: 'transform 0.2s ease-out',
            WebkitTouchCallout: 'none', // Prevents iOS context menu
            userSelect: 'none',         // Prevents text/image selection
            pointerEvents: 'none',      // Prevents the browser from seeing the image as an interactive target
        };

        const handleAudioInteraction = (e: React.MouseEvent) => {
            e.stopPropagation();
            AdService.registerClick();
            if (audioRef.current) {
                if (isAudioPlaying) {
                    audioRef.current.pause();
                    setIsAudioPlaying(false);
                } else {
                    if (audioRef.current.readyState === 0) {
                        audioRef.current.load();
                    }
                    try {
                        if (audioRef.current.currentTime < currentAudioStartTime) {
                            audioRef.current.currentTime = currentAudioStartTime;
                        }
                    } catch (e) {}
                    const playPromise = audioRef.current.play();
                    if (playPromise !== undefined) {
                        playPromise.then(() => setIsAudioPlaying(true)).catch(err => {
                            console.error('Audio interaction play failed:', err);
                            setIsAudioPlaying(false);
                        });
                    } else {
                        setIsAudioPlaying(true);
                    }
                }
            }
        };

        const handleMouseEnter = () => {
            if (audioRef.current && puzzle.audioUrl && !isAudioPlaying) {
                if (audioRef.current.readyState === 0) {
                    audioRef.current.load();
                }
                try {
                    if (audioRef.current.currentTime < currentAudioStartTime) {
                        audioRef.current.currentTime = currentAudioStartTime;
                    }
                } catch(e) {}
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => setIsAudioPlaying(true)).catch(err => console.log('Autoplay prevented:', err));
                } else {
                    setIsAudioPlaying(true);
                }
            }
        };

        const handleMouseLeave = () => {
            if (audioRef.current && isAudioPlaying) {
                audioRef.current.pause();
                setIsAudioPlaying(false);
            }
        };

        return (
            <div 
                className={`relative w-full ${imageHeightClass} bg-black/30 flex items-center justify-center overflow-hidden select-none`}
                onContextMenu={(e) => {
                    e.preventDefault(); // Prevent context menu on the entire container
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {puzzle.audioUrl && (
                    <>
                        {/* Semi-transparent dark overlay so it looks like an audio post */}
                        <div className="absolute inset-0 z-10 bg-black/30 pointer-events-none"></div>
                        
                        {/* Audio Player Controls Overlay */}
                        <div 
                            className={`absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent pt-6 pb-3 px-4 transition-opacity duration-500 flex flex-col gap-2 ${areControlsVisible ? 'opacity-100' : 'opacity-0'}`}
                            onClick={(e) => { e.stopPropagation(); resetControlsTimeout(); }}
                            onMouseMove={resetControlsTimeout}
                            onTouchStart={resetControlsTimeout}
                        >
                            {/* Scrubber */}
                            <div 
                                className="w-full h-3 bg-white/30 rounded-full overflow-hidden cursor-pointer relative"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resetControlsTimeout();
                                    if (audioRef.current) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const percentage = Math.max(0, Math.min(1, x / rect.width));
                                        const newTime = percentage * audioDuration;
                                        audioRef.current.currentTime = Math.max(currentAudioStartTime, Math.min(newTime, currentAudioEndTime));
                                    }
                                }}
                                onTouchStart={(e) => {
                                    e.stopPropagation();
                                    resetControlsTimeout();
                                    if (audioRef.current) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.touches[0].clientX - rect.left;
                                        const percentage = Math.max(0, Math.min(1, x / rect.width));
                                        const newTime = percentage * audioDuration;
                                        audioRef.current.currentTime = Math.max(currentAudioStartTime, Math.min(newTime, currentAudioEndTime));
                                    }
                                }}
                                onTouchMove={(e) => {
                                    e.stopPropagation();
                                    resetControlsTimeout();
                                    if (audioRef.current) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.touches[0].clientX - rect.left;
                                        const percentage = Math.max(0, Math.min(1, x / rect.width));
                                        const newTime = percentage * audioDuration;
                                        audioRef.current.currentTime = Math.max(currentAudioStartTime, Math.min(newTime, currentAudioEndTime));
                                    }
                                }}
                            >
                                <div 
                                    className="h-full bg-pink-500 transition-none pointer-events-none"
                                    style={{ width: `${audioDuration > 0 ? (audioProgress / audioDuration) * 100 : 0}%` }}
                                ></div>
                            </div>
                            
                            <div className="flex items-center justify-between text-white text-xs font-mono drop-shadow-md">
                                <div className="flex gap-2 items-center">
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleAudioInteraction(e); }} 
                                        className="hover:scale-105 transition-transform flex items-center justify-center shrink-0 p-1"
                                    >
                                        <i className={`ph-fill ${isAudioPlaying ? 'ph-pause' : 'ph-play'} text-4xl text-pink-500 drop-shadow-md`}></i>
                                    </button>
                                    <span>{audioProgress > 0 ? new Date(audioProgress * 1000).toISOString().substring(14, 19) : '00:00'}</span>
                                </div>
                                <span>{audioDuration > 0 ? new Date(audioDuration * 1000).toISOString().substring(14, 19) : '00:00'}</span>
                            </div>
                            <audio 
                                ref={audioRef} 
                                src={puzzle.audioUrl} 
                                preload="metadata"
                                playsInline
                                onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                                onPlay={(e) => {
                                    setIsAudioPlaying(true);
                                    const audios = document.querySelectorAll('audio');
                                    audios.forEach(audio => {
                                        if (audio !== e.currentTarget && !audio.paused) {
                                            audio.pause();
                                        }
                                    });
                                }}
                                onPause={() => setIsAudioPlaying(false)}
                                onEnded={() => setIsAudioPlaying(false)}
                                onTimeUpdate={(e) => {
                                    setAudioProgress(e.currentTarget.currentTime);
                                    if (e.currentTarget.currentTime >= currentAudioEndTime) {
                                        e.currentTarget.pause();
                                        try {
                                            e.currentTarget.currentTime = currentAudioStartTime;
                                            setAudioProgress(currentAudioStartTime);
                                        } catch (err) {}
                                        setIsAudioPlaying(false);
                                    }
                                }}
                            />
                        </div>
                    </>
                )}
                <div 
                    className={`frame-container ${imageEdits.frame} ${imageEdits.effect} ${puzzle.audioUrl ? 'opacity-80' : ''}`}
                    style={zoomStyle} // Apply the puppeteer zoom to the wrapper
                >
                    <div className={`filter-wrapper w-full h-full`}>
                        <div className={`w-full h-full ${imageEdits.filter}`}>
                             <img 
                                src={puzzle.imageUrl || appConfig.logoUrl} 
                                alt={puzzle.clue} 
                                className="w-full h-full object-contain max-w-none"
                                style={imageStyle}
                                draggable="false"
                                onDragStart={(e) => e.preventDefault()}
                                onError={(e) => {
                                    e.currentTarget.src = appConfig.logoUrl;
                                    e.currentTarget.onerror = null;
                                }}
                            />
                        </div>
                    </div>
                    {/* Decoy Image Shield - Now The Puppeteer Controller */}
                    {/* 
                        This is a transparent 1x1 GIF stretched over the puzzle.
                        It intercepts touch events for zooming AND blocks context menus.
                    */}
                    <img 
                        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                        className="absolute inset-0 z-10 w-full h-full opacity-0"
                        alt=""
                        draggable="false"
                        onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                        }}
                        // Puppeteer Handlers
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{ touchAction: zoomState.scale > 1 ? 'none' : 'pan-y' }} // Allows vertical scrolling unless zoomed
                    />
                </div>
            </div>
        )
    };

    const renderAnswerInputs = (isTimed = false) => {
        const words = puzzle.answer.split(' ');
        const canType = (!isOwnPost || canPlayAIChallenge || isPreview);
        
        return (
             <div className="flex flex-wrap justify-center items-center px-1 relative gap-y-4">
                {words.map((word, wordIndex) => (
                    <div key={wordIndex} className="flex flex-nowrap items-center py-1 mx-1 sm:mx-2">
                        {word.split('').map((char, charIndexInWord) => {
                            const globalIndex = words.slice(0, wordIndex).join(' ').length + (wordIndex > 0 ? 1 : 0) + charIndexInWord;
                            const isRevealed = revealedIndices.has(globalIndex);
                            return (
                                <input
                                    key={globalIndex}
                                    ref={el => { inputsRef.current[globalIndex] = el; }}
                                    type="text"
                                    maxLength={1}
                                    value={(guess[globalIndex] || '').trim()}
                                    onChange={e => handleInputChange(e, globalIndex)}
                                    onClick={isClickable ? handleAnswerCardClick : stopPropagation}
                                    onKeyDown={e => handleInputKeyDown(e, globalIndex)}
                                    disabled={!canType || (isRevealed && !isTimed)}
                                    className={`h-7 w-5 sm:h-9 sm:w-7 flex-shrink-0 text-center font-mono font-bold text-sm sm:text-lg border-0 border-b-2 bg-transparent focus:outline-none focus:ring-0 rounded-none mx-0.5 ${
                                        (isRevealed && !isTimed) ? 'aurora-text shimmering-text border-purple-400 cursor-not-allowed' : 'text-black border-gray-400 focus:border-pink-500'
                                    } ${!canType ? 'cursor-not-allowed bg-gray-100 opacity-60' : ''}`}
                                    autoComplete="off"
                                    spellCheck="false"
                                    aria-label={`Character ${globalIndex + 1} of the answer`}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <div ref={postCardRef} style={{ backgroundColor: puzzle.postColor || 'var(--post-bg-color)' }} className={`relative rounded-xl shadow-md overflow-hidden text-white ${userProfile?.enableShimmerEffect && 'postcard-shimmer-container'}`}>
                {!hideHeader && (
                    <div className="p-4 flex items-center justify-between" onClick={isClickable ? handleCardClick : stopPropagation}>
                        <div className="flex items-center space-x-3 flex-grow min-w-0">
                            <div className="relative flex-shrink-0">
                                <button 
                                    onClick={handleProfileClick}
                                    className={`w-10 h-10 rounded-full ${!isPreview ? 'cursor-pointer' : ''}`}
                                    aria-label={`View profile of ${creatorDisplayName}`}
                                >
                                    <img 
                                        src={creatorPhotoURL} 
                                        alt={creatorDisplayName} 
                                        className={`w-full h-full rounded-full object-cover`}
                                    />
                                </button>
                                {isCreatorOnline && (
                                    <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                                )}
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center space-x-2">
                                    <p onClick={handleProfileClick} className={`font-bold hover:underline truncate ${!isPreview ? 'cursor-pointer' : ''}`}>{creatorDisplayName}</p>
                                    {!isOwnPost && userProfile?.uid !== puzzle.creatorId && !isPreview && (
                                        <button 
                                            onClick={handleFollowClick}
                                            className="text-sm font-bold aurora-text transition-opacity hover:opacity-80"
                                        >
                                            {isFollowingCreator ? 'Friend' : 'Add Friend'}
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <p className="text-xs font-semibold aurora-text">{puzzle.category}</p>
                                    {puzzle.isSponsored && (
                                         <>
                                             <span className="text-gray-400 text-xs">•</span>
                                             <div className="flex items-center gap-1 text-xs font-bold text-yellow-400">
                                                 <i className="ph-fill ph-star"></i>
                                                 <span>SPONSORED</span>
                                             </div>
                                         </>
                                    )}
                                    {puzzle.type === 'ai' && (
                                         <>
                                            <span className="text-gray-400 text-xs">•</span>
                                            <div className="flex items-center gap-1 text-xs font-semibold text-yellow-300">
                                                <span>AI Hosted ✨</span>
                                            </div>
                                        </>
                                    )}
                                    {puzzle.groupId && (
                                        <>
                                            <span className="text-gray-400 text-xs">•</span>
                                            <div className="flex items-center gap-1 text-xs font-semibold text-cyan-400">
                                                <i className="ph-duotone ph-users-three"></i>
                                                <span>Group Post</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Only show menu if logged in */}
                        {userProfile && !isPreview && (
                            <div className="relative" ref={optionsMenuRef} onClick={stopPropagation}>
                                <button onClick={() => setShowOptionsMenu(!showOptionsMenu)} className="text-gray-300 hover:text-white p-2">
                                    <i className="ph-duotone ph-dots-three-outline-vertical text-xl"></i>
                                </button>
                                {showOptionsMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-[var(--primary-dark-purple)] border border-white/20 backdrop-blur-md rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in text-white">
                                        {isOwnPost ? (
                                            <>
                                                {onEdit && (
                                                    <button 
                                                        onClick={() => { onEdit(puzzle); setShowOptionsMenu(false); }} 
                                                        className="w-full text-left px-4 py-3 text-sm hover:bg-white/10 flex items-center gap-2 transition-colors border-b border-white/10"
                                                    >
                                                        <i className="ph-duotone ph-pencil-simple text-lg text-purple-300"></i>
                                                        <span>Edit Post</span>
                                                    </button>
                                                )}
                                                <button 
                                                    ref={deleteButtonRef}
                                                    onClick={handleDeleteClick} 
                                                    className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/20 flex items-center gap-2 transition-colors"
                                                >
                                                    <i className="ph-duotone ph-trash text-lg"></i>
                                                    <span>Delete Post</span>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    ref={reportButtonRef}
                                                    onClick={(e) => {
                                                        setReportModalTriggerRect(e.currentTarget.getBoundingClientRect());
                                                        setReportModalOpen(true);
                                                        setShowOptionsMenu(false);
                                                    }} 
                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-white/10 flex items-center gap-2 transition-colors border-b border-white/10 text-orange-300"
                                                >
                                                    <i className="ph-duotone ph-warning-circle text-lg"></i>
                                                    <span>Report Post</span>
                                                </button>
                                                <button
                                                    ref={blockButtonRef}
                                                    onClick={handleBlockUserClick} 
                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-white/10 flex items-center gap-2 transition-colors text-gray-300"
                                                >
                                                    <i className="ph-duotone ph-prohibit text-lg"></i>
                                                    <span>Block User</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                <div 
                    onClick={isClickable ? handleCardClick : !isSolved ? handleAnswerCardClick : undefined} 
                    className={`${isClickable ? 'cursor-pointer' : !isSolved ? 'cursor-text' : ''}`}
                >
                    {renderPuzzleContent()}
                </div>
                
                {(puzzle.showClue ?? true) && puzzle.clue && (
                    <div className={`p-4 ${isClickable ? 'cursor-pointer' : !isSolved ? 'cursor-text' : ''}`} onClick={isClickable ? handleCardClick : !isSolved ? handleAnswerCardClick : undefined}>
                        <TaggedContent baseClassName="font-semibold aurora-text" text={puzzle.clue} userId={puzzle.creatorId} />
                    </div>
                )}

                {!isPreview && (
                    <div className="px-4 pb-2" onClick={isClickable ? handleCardClick : stopPropagation}>
                        {puzzle.type === 'timed' ? (
                            (() => {
                                const gameStartTimeMs = puzzle.gameStartTime?.toMillis();
                                const durationSecs = puzzle.timerDuration || 60;
                                const secondsElapsed = gameStartTimeMs ? Math.floor((now - gameStartTimeMs) / 1000) : 0;
                                const timeLeft = Math.max(0, durationSecs - secondsElapsed);
                                const isGameStarted = !!gameStartTimeMs;
                                const isGameOngoing = isGameStarted && timeLeft > 0;
                                const isGameOver = isGameStarted && timeLeft <= 0;
                                const playerHasCompleted = userProfile && puzzle.playerScores && puzzle.playerScores[userProfile.uid];
                                
                                const handleTimedGuessSubmit = (e: React.FormEvent) => {
                                    e.preventDefault();
                                    if (!userProfile || playerHasCompleted || isGameOver || (isOwnPost && !canPlayAIChallenge) || guess.replace(/\s/g, '').length === 0) return;

                                    if (guess.toLowerCase() === puzzle.answer.toLowerCase()) {
                                        const pointsAwarded = 5;
                                        if(puzzle.groupId) {
                                            recordPlayerScore(puzzle.id, userProfile.uid, pointsAwarded, TimestampValue.now());
                                            updateGroupScore(puzzle.groupId, pointsAwarded, puzzle.id);
                                        } else {
                                            recordPlayerScore(puzzle.id, userProfile.uid, pointsAwarded, TimestampValue.now());
                                            updateScore(pointsAwarded, puzzle);
                                        }
                                        const soundToPlay = userProfile?.correctGuessSound || 'Success Chime';
                                        if (soundToPlay !== 'None') playSound(soundToPlay);
                                    } else {
                                        const pointsDeducted = 1;
                                        if (puzzle.groupId) {
                                            deductGroupPoints(puzzle.groupId, pointsDeducted);
                                        } else {
                                            deductPoints(pointsDeducted);
                                        }

                                        if (!isOwnPost) {
                                            addPointsToUser(puzzle.creatorId, 1);
                                        }

                                        const soundToPlay = userProfile?.wrongGuessSound || 'Buzz';
                                        if(soundToPlay !== 'None') playSound(soundToPlay);
                                        setGuess(initialRevealedString);
                                    }
                                };
                                
                                const sortedPlayers = Object.entries(puzzle.playerScores || {})
                                    .map(([uid, data]) => {
                                        const playerData = data as { score: number; completedAt: Timestamp };
                                        return { uid, score: playerData.score, completedAt: playerData.completedAt };
                                    })
                                    .sort((a, b) => b.score - a.score);

                                if (isGameOngoing) {
                                    return (
                                        <div>
                                            <div className="text-center mb-4">
                                                <p className="text-sm text-gray-300">Time Remaining</p>
                                                <p className="font-orbitron text-4xl font-bold">{formatTime(timeLeft)}</p>
                                            </div>
                                            {playerHasCompleted ? (
                                                <div className="bg-white border-l-4 border-emerald-500 text-emerald-600 p-4 rounded-lg text-center shadow-inner">
                                                    <p className="font-bold">You scored {playerHasCompleted.score} points!</p>
                                                    <p className="text-sm">Waiting for others to finish...</p>
                                                </div>
                                            ) : (
                                                <form onSubmit={handleTimedGuessSubmit} className="flex items-center space-x-2">
                                                     <div className="flex-grow p-2 rounded-lg bg-white cursor-text min-w-0" onClick={handleAnswerCardClick}>
                                                        {renderAnswerInputs(true)}
                                                    </div>
                                                    <button 
                                                        type="submit" 
                                                        disabled={(!userProfile) || (isOwnPost && !canPlayAIChallenge) || guess.replace(/\s/g, '').length === 0} 
                                                        className="text-white font-bold p-2 rounded-lg text-2xl disabled:text-gray-500 transition-colors hover:text-[var(--accent-pink)]"
                                                    >
                                                        <i className="ph-duotone ph-paper-plane-tilt"></i>
                                                    </button>
                                                </form>
                                            )}
                                        </div>
                                    );
                                }

                                if (isGameOver) {
                                    return (
                                        <div>
                                            <div className="text-center mb-4 p-4 rounded-lg bg-red-800/50">
                                                <p className="text-2xl font-bold">Time's Up!</p>
                                                <p className="font-semibold">The correct answer was: <span className="font-bold aurora-text">{puzzle.answer}</span></p>
                                            </div>
                                            <div className="px-4 pb-2">
                                                <h4 className="font-bold mb-2 text-center">Final Results</h4>
                                                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                                    {sortedPlayers.length > 0 ? sortedPlayers.map(({ uid, score }, index) => {
                                                        const player = allUsers.find(u => u.uid === uid);
                                                        if (!player) return null;
                                                        return (
                                                            <div key={uid} className="flex items-center bg-black/10 p-2 rounded-lg">
                                                                <span className="font-bold w-6">{index + 1}.</span>
                                                                <img src={player.photoURL} className="w-8 h-8 rounded-full object-cover mx-2"/>
                                                                <p className="flex-1 font-semibold truncate">{player.displayName}</p>
                                                                <p className="font-bold">{score} pts</p>
                                                            </div>
                                                        )
                                                    }) : <p className="text-sm text-gray-400 text-center">No one solved it in time.</p>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                
                                return null;
                            })()
                        ) : isSolved ? (
                            <div className="bg-white border-l-4 border-emerald-500 text-emerald-600 p-4 rounded-lg text-center shadow-inner space-y-2 relative">
                                <div className="flex items-center justify-center gap-2">
                                    <p className="font-bold">Puzzle Solved!</p>
                                    {!isAnswerRevealed ? (
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                AdService.registerClick();
                                                setIsAnswerRevealed(true);
                                            }}
                                            className="text-emerald-600 hover:text-emerald-800 transition-colors"
                                            title="Reveal Answer"
                                        >
                                            <i className="ph-duotone ph-eye text-xl"></i>
                                        </button>
                                    ) : (
                                        <span className="font-bold aurora-text ml-2">{puzzle.answer}</span>
                                    )}
                                </div>
                                {puzzle.isSponsored && (puzzle.promoLink || puzzle.promoCoupon) && (
                                    <div className="mt-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100 animate-fade-in">
                                        <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Special Offer</p>
                                        {puzzle.promoCoupon && (
                                            <p className="text-sm font-mono font-bold bg-white px-2 py-1 rounded border border-emerald-200 mb-2">
                                                CODE: {puzzle.promoCoupon}
                                            </p>
                                        )}
                                        {puzzle.promoLink && (
                                            <a 
                                                href={puzzle.promoLink} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="inline-block w-full bg-emerald-600 text-white text-xs font-bold py-2 px-4 rounded-lg hover:bg-emerald-500 transition-colors"
                                            >
                                                Claim Offer <i className="ph-bold ph-arrow-square-out ml-1"></i>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleGuessSubmit} className="flex items-center space-x-2">
                                <div className="flex-grow p-2 rounded-lg bg-white cursor-text min-w-0" onClick={handleAnswerCardClick}>
                                    {renderAnswerInputs()}
                                </div>
                                <div className={`flex flex-col self-stretch ${puzzle.areCluesAllowed !== false ? 'space-y-2 justify-between' : 'justify-end'}`}>
                                    {puzzle.areCluesAllowed !== false && (!isOwnPost || canPlayAIChallenge || isPreview) && (
                                        <button
                                            ref={clueButtonRef}
                                            type="button"
                                            onClick={(e) => {
                                                if(!userProfile) { pageContext?.switchPage(Page.Login, 'Sign In'); return; }
                                                setClueModalTriggerRect(e.currentTarget.getBoundingClientRect());
                                                setClueModalOpen(true);
                                            }}
                                            className="w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110 active:scale-95 flex-shrink-0"
                                            style={{ background: 'var(--aurora-gradient)' }}
                                            title="Use a Clue" aria-label="Use a Clue"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" className="w-6 h-6 text-white fill-current">
                                                <path d="M140,180a12,12,0,1,1-12-12A12,12,0,0,1,140,180ZM128,72c-22.06,0-40,16.15-40,36v4a8,8,0,0,0,16,0v-4c0-11,10.77-20,24-20s24,9,24,20-10.77,20-24,20a8,8,0,0,0-8,8v8a8,8,0,0,0,16,0v-.72c18.24-3.35,32-17.91,32-35.28C168,88.15,150.06,72,128,72Z"></path>
                                            </svg>
                                        </button>
                                    )}
                                    <button 
                                        type="submit" 
                                        disabled={(!isPreview && isOwnPost && !canPlayAIChallenge) || guess === initialRevealedString || guess.trim().length === 0} 
                                        className="text-white font-bold p-2 rounded-lg text-2xl disabled:text-gray-500 transition-colors hover:text-[var(--accent-pink)]"
                                    >
                                        <i className="ph-duotone ph-paper-plane-tilt"></i>
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
                
                {isPreview && (
                    <div className="px-4 pb-2" onClick={stopPropagation}>
                        <form onSubmit={handleGuessSubmit} className="flex items-center space-x-2">
                            <div className="flex-grow p-2 rounded-lg bg-white cursor-text min-w-0" onClick={handleAnswerCardClick}>
                                {renderAnswerInputs()}
                            </div>
                            <div className="flex flex-col self-stretch justify-end">
                                <button 
                                    type="submit" 
                                    className="text-white font-bold p-2 rounded-lg text-2xl disabled:text-gray-500 transition-colors hover:text-[var(--accent-pink)]"
                                    disabled={guess === initialRevealedString || guess.trim().length === 0}
                                >
                                    <i className="ph-duotone ph-paper-plane-tilt"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Show Reactions/Comments only if NOT preview and NOT hidden */}
                {!isPreview && !hideActions && (
                    <div className="px-4 py-3 flex items-center justify-between text-white" onClick={stopPropagation}>
                        <div className="flex items-center gap-4">
                            <button onClick={() => handleReaction('likes')} className="flex items-center gap-1 hover:text-blue-400 group transition-colors">
                                <i className={`ph-duotone ph-thumbs-up text-2xl group-active:scale-125 transition-transform ${userProfile && puzzle.likes.includes(userProfile.uid) ? 'text-blue-400 fill-current' : 'text-white'}`}></i>
                                <span className="font-bold text-sm">{puzzle.likes.length}</span>
                            </button>
                            <button onClick={() => handleReaction('dislikes')} className="flex items-center gap-1 hover:text-purple-300 group transition-colors">
                                <i className={`ph-duotone ph-thumbs-down text-2xl group-active:scale-125 transition-transform ${userProfile && puzzle.dislikes.includes(userProfile.uid) ? 'text-purple-300 fill-current' : 'text-white'}`}></i>
                                <span className="font-bold text-sm">{puzzle.dislikes.length}</span>
                            </button>
                            <button onClick={() => handleReaction('hearts')} className="flex items-center gap-1 hover:text-pink-500 group transition-colors">
                                <i className={`ph-duotone ph-heart text-2xl group-active:scale-125 transition-transform ${userProfile && puzzle.hearts.includes(userProfile.uid) ? 'text-pink-500 fill-current' : 'text-white'}`}></i>
                                <span className="font-bold text-sm">{puzzle.hearts.length}</span>
                            </button>
                            <button onClick={handleCommentToggle} className="flex items-center gap-1 hover:text-blue-300 group transition-colors">
                                <i className="ph-duotone ph-chat-circle text-2xl text-white group-active:scale-125 transition-transform"></i>
                                <span className="font-bold text-sm">{puzzle.comments.length > 0 ? puzzle.comments.length : ''}</span>
                            </button>
                            <button onClick={(e) => { 
                                if (!userProfile) { pageContext?.switchPage(Page.Login, 'Sign In'); return; }
                                setShareModalTriggerRect(e.currentTarget.getBoundingClientRect()); setShareModalOpen(true); 
                            }} className="flex items-center gap-1 hover:text-green-400 group transition-colors">
                                <i className="ph-duotone ph-paper-plane-right text-2xl text-white group-active:scale-125 transition-transform"></i>
                            </button>
                        </div>
                        
                        {/* Only show solver counts if logged in to prevent clutter for guests */}
                        {userProfile && (
                            <div className="flex items-center gap-3 ml-2">
                                <button onClick={() => pageContext?.launchGuessersModal('Guessed Correctly', puzzle.correctGuessers || [])} className="flex items-center gap-1 hover:text-green-400">
                                    <i className="ph-duotone ph-check-circle text-xl text-green-400"></i>
                                    <span className="text-sm font-bold">{puzzle.correctGuessers?.length || 0}</span>
                                </button>
                                <button onClick={() => {
                                    const extraInfo = isOwnPost && puzzle.incorrectGuesses 
                                        ? [...puzzle.incorrectGuesses].sort((a,b) => b.timestamp - a.timestamp).map(g => ({ userId: g.userId, subText: g.guess }))
                                        : undefined;
                                    pageContext?.launchGuessersModal('Guessed Incorrectly', puzzle.incorrectGuessers || [], extraInfo);
                                }} className="flex items-center gap-1 hover:text-red-400">
                                    <i className="ph-duotone ph-x-circle text-xl text-red-400"></i>
                                    <span className="text-sm font-bold">{puzzle.incorrectGuessers?.length || 0}</span>
                                </button>
                            </div>
                        )}
                        
                    </div>
                )}

                {showComments && !hideComments && <CommentSection 
                    puzzleId={puzzle.id} 
                    initialComments={puzzle.comments} 
                    answer={puzzle.answer}
                    creatorId={puzzle.creatorId}
                    onClick={stopPropagation} 
                />}

            </div>
            <ReportPostModal isOpen={isReportModalOpen} onClose={() => setReportModalOpen(false)} onSubmit={handleReportSubmit} triggerRect={reportModalTriggerRect}/>
            <WrongGuessModal
                isOpen={showWrongGuess}
                onClose={handleCloseFeedbackModals}
                onUseClue={handleOpenClueModalFromWrongGuess}
                areCluesAllowed={puzzle.areCluesAllowed !== false}
            />
            <UseClueModal
                isOpen={isClueModalOpen}
                onClose={() => setClueModalOpen(false)}
                onUseClue={handleUseClue}
                onPurchaseClues={handlePurchaseClues}
                onWatchAd={handleWatchAd}
                clueCount={userProfile?.clues ?? 0}
                triggerRect={clueModalTriggerRect}
            />
            <ShareModal 
                isOpen={isShareModalOpen} 
                onClose={() => setShareModalOpen(false)} 
                puzzle={puzzle} 
                triggerRect={shareModalTriggerRect}
            />
             <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={confirmModalProps.onConfirm}
                title={confirmModalProps.title}
                message={confirmModalProps.message}
                triggerRect={confirmModalProps.triggerRect}
                confirmText={confirmModalProps.confirmText}
            />
            <AIConsentModal
                isOpen={showAIConsentModal}
                onClose={() => setShowAIConsentModal(false)}
                onConsentGranted={() => handleGetAIHint()}
            />
        </>
    );
};

export default memo(PostCard);
